import api from './api';

/**
 * Gulag Gaming System API Service
 * Handles all API calls for challenges, sessions, rankings, LFG, and games
 */

// Simple in-memory cache with TTL
const cache = new Map();
const CACHE_TTL = {
    MY_STATS: 30000, // 30 seconds
    RANKINGS: 60000, // 60 seconds
};

const getCacheKey = (prefix, ...args) => {
    return `${prefix}:${args.filter(a => a !== null && a !== undefined).join(':')}`;
};

const getFromCache = (key) => {
    const cached = cache.get(key);
    if (!cached) return null;
    
    const now = Date.now();
    if (now > cached.expiry) {
        cache.delete(key);
        return null;
    }
    
    return cached.data;
};

const setInCache = (key, data, ttl) => {
    cache.set(key, {
        data,
        expiry: Date.now() + ttl
    });
};

const clearCachePattern = (pattern) => {
    const keys = Array.from(cache.keys());
    keys.forEach(key => {
        if (key.startsWith(pattern)) {
            cache.delete(key);
        }
    });
};

export const clearGulagCache = () => {
    cache.clear();
};

// ==================== Game Management ====================

export const getGames = async (status = 'active', timeTrial = null) => {
    try {
        const params = { status };
        if (timeTrial !== null) {
            params.time_trial = timeTrial;
        }
        const response = await api.get('/api/gulag/games', { params });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching games:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch games',
            data: []
        };
    }
};

export const getGame = async (gameId) => {
    try {
        const response = await api.get(`/api/gulag/games/${gameId}`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching game:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch game',
            data: null
        };
    }
};

export const createGame = async (gameData) => {
    try {
        const response = await api.post('/api/gulag/games', gameData);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error creating game:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to create game'
        };
    }
};

export const updateGame = async (gameId, updateData) => {
    try {
        const response = await api.put(`/api/gulag/games/${gameId}`, updateData);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating game:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to update game'
        };
    }
};

export const uploadGameImage = async (gameId, imageFile) => {
    try {
        const formData = new FormData();
        formData.append('file', imageFile);
        
        const response = await api.post(`/api/gulag/games/${gameId}/image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error uploading game image:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to upload game image'
        };
    }
};

export const getGamePointsConfig = async (gameId) => {
    try {
        const response = await api.get(`/api/gulag/games/${gameId}/points-config`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching game points config:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch game points configuration',
            data: null
        };
    }
};

export const updateGamePointsConfig = async (gameId, gameType, pointsConfig, matchLimits) => {
    try {
        const response = await api.put(`/api/gulag/games/${gameId}/points-config`, {
            game_type: gameType,
            points_config: pointsConfig,
            match_limits: matchLimits
        });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating game points config:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to update game points configuration'
        };
    }
};

// ==================== Challenge Management ====================

export const getIncomingChallenges = async (statusFilter = null) => {
    try {
        const params = statusFilter ? { status_filter: statusFilter } : {};
        const response = await api.get('/api/gulag/challenges/incoming', { params });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching incoming challenges:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch incoming challenges',
            data: []
        };
    }
};

export const getOutgoingChallenges = async (statusFilter = null) => {
    try {
        const params = statusFilter ? { status_filter: statusFilter } : {};
        const response = await api.get('/api/gulag/challenges/outgoing', { params });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching outgoing challenges:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch outgoing challenges',
            data: []
        };
    }
};

export const createChallenge = async (challengeData) => {
    try {
        const response = await api.post('/api/gulag/challenges', challengeData);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error creating challenge:', error);
        
        // Handle validation errors (422) which return an array
        let errorMessage = 'Failed to create challenge';
        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            if (typeof detail === 'string') {
                errorMessage = detail;
            } else if (Array.isArray(detail)) {
                // Pydantic validation errors
                errorMessage = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
            }
        }
        
        return {
            success: false,
            error: errorMessage
        };
    }
};

export const acceptChallenge = async (challengeId) => {
    try {
        const response = await api.put(`/api/gulag/challenges/${challengeId}/accept`);
        // Clear cache on challenge state change
        clearCachePattern('MY_STATS');
        clearCachePattern('RANKINGS');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error accepting challenge:', error);
        
        // Handle validation errors properly
        let errorMessage = 'Failed to accept challenge';
        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            if (typeof detail === 'string') {
                errorMessage = detail;
            } else if (Array.isArray(detail)) {
                errorMessage = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
            }
        }
        
        return {
            success: false,
            error: errorMessage
        };
    }
};

export const declineChallenge = async (challengeId) => {
    try {
        const response = await api.put(`/api/gulag/challenges/${challengeId}/decline`);
        // Clear cache on challenge state change
        clearCachePattern('MY_STATS');
        clearCachePattern('RANKINGS');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error declining challenge:', error);
        
        // Handle validation errors properly
        let errorMessage = 'Failed to decline challenge';
        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            if (typeof detail === 'string') {
                errorMessage = detail;
            } else if (Array.isArray(detail)) {
                errorMessage = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
            }
        }
        
        return {
            success: false,
            error: errorMessage
        };
    }
};

export const completeChallenge = async (challengeId, resultData) => {
    try {
        const response = await api.post(`/api/gulag/challenges/${challengeId}/complete`, resultData);
        // Clear cache on match completion - this affects stats and rankings
        clearCachePattern('MY_STATS');
        clearCachePattern('RANKINGS');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error completing challenge:', error);
        
        // Handle validation errors properly
        let errorMessage = 'Failed to complete challenge';
        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            if (typeof detail === 'string') {
                errorMessage = detail;
            } else if (Array.isArray(detail)) {
                errorMessage = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
            }
        }
        
        return {
            success: false,
            error: errorMessage
        };
    }
};

export const getMatchCount = async (eventId, gameId, opponentId) => {
    try {
        const response = await api.get(
            `/api/gulag/match-count/${eventId}/${gameId}/${opponentId}`
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching match count:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to get match count',
            data: { match_count: 0, max_matches: 5, remaining: 5 }
        };
    }
};

export const getTotalMatchCount = async (eventId, gameId) => {
    try {
        const response = await api.get(
            `/api/gulag/total-match-count/${eventId}/${gameId}`
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching total match count:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to get total match count',
            data: { total_match_count: 0, max_total_matches: null, remaining: null }
        };
    }
};

export const getBatchMatchCounts = async (batchRequests) => {
    try {
        const response = await api.post('/api/gulag/match-counts/batch', batchRequests);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching batch match counts:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to get match counts',
            data: {}
        };
    }
};

// ==================== Match History ====================

export const getMatchHistory = async (limit = 50) => {
    try {
        const response = await api.get('/api/gulag/matches/history', { params: { limit } });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching match history:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch match history',
            data: []
        };
    }
};

export const getEventMatches = async (eventId) => {
    try {
        const response = await api.get(`/api/gulag/events/${eventId}/matches`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching event matches:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch event matches',
            data: []
        };
    }
};

// ==================== Rankings & Statistics ====================

export const getGlobalRankings = async (participantType = null, limit = 100) => {
    const cacheKey = getCacheKey('RANKINGS_GLOBAL', participantType, limit);
    const cached = getFromCache(cacheKey);
    if (cached) {
        return { success: true, data: cached };
    }

    try {
        const params = {};
        if (participantType) params.participant_type = participantType;
        if (limit) params.limit = limit;
        
        const response = await api.get('/api/gulag/rankings/global', { params });
        setInCache(cacheKey, response.data, CACHE_TTL.RANKINGS);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching global rankings:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch global rankings',
            data: []
        };
    }
};

export const getEventRankings = async (eventId, participantType = null, limit = 100) => {
    const cacheKey = getCacheKey('RANKINGS_EVENT', eventId, participantType, limit);
    const cached = getFromCache(cacheKey);
    if (cached) {
        return { success: true, data: cached };
    }

    try {
        const params = {};
        if (participantType) params.participant_type = participantType;
        if (limit) params.limit = limit;
        
        const response = await api.get(`/api/gulag/events/${eventId}/rankings`, { params });
        setInCache(cacheKey, response.data, CACHE_TTL.RANKINGS);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching event rankings:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch event rankings',
            data: []
        };
    }
};

export const getUserStats = async (userId) => {
    try {
        const response = await api.get(`/api/gulag/rankings/user/${userId}`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching user stats:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch user statistics',
            data: null
        };
    }
};

export const getMyStats = async () => {
    const cacheKey = getCacheKey('MY_STATS');
    const cached = getFromCache(cacheKey);
    if (cached) {
        return { success: true, data: cached };
    }

    try {
        const response = await api.get('/api/gulag/rankings/me');
        setInCache(cacheKey, response.data, CACHE_TTL.MY_STATS);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching my stats:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch your statistics',
            data: null
        };
    }
};

// ==================== Event Management ====================

export const getGulagEvents = async () => {
    try {
        const response = await api.get('/api/gulag/events');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching Gulag events:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch Gulag events',
            data: []
        };
    }
};

export const getAllEventsForAdmin = async () => {
    try {
        const response = await api.get('/api/gulag/events/all');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching all events:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch events',
            data: []
        };
    }
};

export const updateEventGulagSettings = async (eventId, gulagEnabled, gulagPointsMultiplier = 1.0) => {
    try {
        const response = await api.put(
            `/api/gulag/events/${eventId}/gulag-settings`,
            null,
            {
                params: {
                    gulag_enabled: gulagEnabled,
                    gulag_points_multiplier: gulagPointsMultiplier
                }
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating event Gulag settings:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to update Gulag settings'
        };
    }
};

// ==================== Session Management ====================

export const getEventSessions = async (eventId) => {
    try {
        const response = await api.get(`/api/gulag/events/${eventId}/sessions`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching event sessions:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch event sessions',
            data: []
        };
    }
};

export const getNextSession = async (eventId) => {
    try {
        const response = await api.get(`/api/gulag/events/${eventId}/sessions/next`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching next session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch next session',
            data: null
        };
    }
};

export const createSession = async (sessionData) => {
    try {
        const response = await api.post('/api/gulag/sessions', sessionData);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error creating session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to create session'
        };
    }
};

export const joinSession = async (sessionId) => {
    try {
        const response = await api.post(`/api/gulag/sessions/${sessionId}/join`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error joining session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to join session'
        };
    }
};

export const leaveSession = async (sessionId) => {
    try {
        const response = await api.delete(`/api/gulag/sessions/${sessionId}/leave`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error leaving session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to leave session'
        };
    }
};

export const addParticipantToSession = async (sessionId, userId) => {
    try {
        const response = await api.post(`/api/gulag/sessions/${sessionId}/participants/${userId}`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error adding participant to session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to add participant to session'
        };
    }
};

export const updateSession = async (sessionId, sessionData) => {
    try {
        const response = await api.put(`/api/gulag/sessions/${sessionId}`, sessionData);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to update session'
        };
    }
};

export const deleteSession = async (sessionId) => {
    try {
        const response = await api.delete(`/api/gulag/sessions/${sessionId}`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error deleting session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to delete session'
        };
    }
};

// ==================== Session Configuration & Management ====================

export const configureSessionGroups = async (sessionId, groupsConfig) => {
    try {
        const response = await api.put(`/api/gulag/sessions/${sessionId}/configure-groups`, {
            groups: groupsConfig
        });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error configuring session groups:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to configure groups'
        };
    }
};

export const assignGroupLeader = async (sessionId, groupId, leaderId) => {
    try {
        const response = await api.put(
            `/api/gulag/sessions/${sessionId}/groups/${groupId}/leader`,
            { leader_id: leaderId }
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error assigning group leader:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to assign group leader'
        };
    }
};

export const startSession = async (sessionId) => {
    try {
        const response = await api.post(`/api/gulag/sessions/${sessionId}/start`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error starting session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to start session'
        };
    }
};

export const endSession = async (sessionId) => {
    try {
        const response = await api.post(`/api/gulag/sessions/${sessionId}/end`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error ending session:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to end session'
        };
    }
};

export const getSessionLobby = async (sessionId) => {
    try {
        const response = await api.get(`/api/gulag/sessions/${sessionId}/lobby`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching session lobby:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch session lobby',
            data: null
        };
    }
};

export const submitGroupRankings = async (sessionId, groupId, rankings) => {
    try {
        const response = await api.post(
            `/api/gulag/sessions/${sessionId}/groups/${groupId}/rankings`,
            rankings
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error submitting rankings:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to submit rankings'
        };
    }
};

export const getSessionResults = async (sessionId) => {
    try {
        const response = await api.get(`/api/gulag/sessions/${sessionId}/results`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching session results:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch session results',
            data: null
        };
    }
};

// ==================== LFG (Looking for Game/Group) ====================

export const getActiveLFG = async (eventId) => {
    try {
        const response = await api.get(`/api/gulag/events/${eventId}/lfg`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching LFG posts:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch LFG posts',
            data: []
        };
    }
};

export const createLFG = async (lfgData) => {
    try {
        const response = await api.post('/api/gulag/lfg', lfgData);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error creating LFG post:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to create LFG post'
        };
    }
};

export const closeLFG = async (lfgId) => {
    try {
        const response = await api.delete(`/api/gulag/lfg/${lfgId}`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error closing LFG post:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to close LFG post'
        };
    }
};

// ==================== User Game Preferences ====================

export const getMyPreferences = async () => {
    try {
        const response = await api.get('/api/gulag/preferences/me');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching preferences:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch preferences',
            data: []
        };
    }
};

export const setGamePreference = async (gameId, preferenceData) => {
    try {
        const response = await api.post(`/api/gulag/preferences/${gameId}`, preferenceData);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error setting game preference:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to set game preference'
        };
    }
};

export const removeGamePreference = async (gameId) => {
    try {
        const response = await api.delete(`/api/gulag/preferences/${gameId}`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error removing game preference:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to remove game preference'
        };
    }
};

export const toggleGameAvailability = async (gameId, available) => {
    try {
        const response = await api.put(`/api/gulag/preferences/${gameId}/toggle`, null, {
            params: { available }
        });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error toggling game availability:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to toggle game availability'
        };
    }
};

export const getAvailablePlayers = async (gameId) => {
    try {
        const response = await api.get(`/api/gulag/available-players/${gameId}`);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching available players:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch available players',
            data: []
        };
    }
};

export const getAvailablePlayersFiltered = async (eventId, gameId) => {
    try {
        const response = await api.get(
            `/api/gulag/available-players-filtered/${eventId}/${gameId}`
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching filtered available players:', error);
        return {
            success: false,
            error: error.response?.data?.detail || 'Failed to fetch available players',
            data: []
        };
    }
};

// Export all functions as a single service object
const gulagService = {
    // Events
    getGulagEvents,
    getAllEventsForAdmin,
    updateEventGulagSettings,
    
    // Games
    getGames,
    getGame,
    createGame,
    updateGame,
    uploadGameImage,
    getGamePointsConfig,
    updateGamePointsConfig,
    
    // Challenges
    getIncomingChallenges,
    getOutgoingChallenges,
    createChallenge,
    acceptChallenge,
    declineChallenge,
    completeChallenge,
    
    // Matches
    getMatchHistory,
    getEventMatches,
    
    // Rankings
    getGlobalRankings,
    getEventRankings,
    getUserStats,
    getMyStats,
    
    // Sessions
    getEventSessions,
    getNextSession,
    createSession,
    joinSession,
    leaveSession,
    addParticipantToSession,
    updateSession,
    deleteSession,
    
    // Session Configuration & Management
    configureSessionGroups,
    assignGroupLeader,
    startSession,
    endSession,
    getSessionLobby,
    submitGroupRankings,
    getSessionResults,
    
    // LFG
    getActiveLFG,
    createLFG,
    closeLFG,
    
    // Preferences
    getMyPreferences,
    setGamePreference,
    removeGamePreference,
    toggleGameAvailability,
    getAvailablePlayers,
    getAvailablePlayersFiltered,
    
    // Match Counts
    getMatchCount,
    getTotalMatchCount,
    getBatchMatchCounts
};

export default gulagService;

